import React, { useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'
import LoginForm from '../LoginForm'
import MfaVerification from '../MfaVerification'
import Copyright from '../Copyright'
import LogoDisplay from '../LogoDisplay'
import { gql, useMutation } from 'urql'
import theme from '../../theme'
import { AUTH_STATUS } from '../../../types/constants'

interface Props {
  hasPermissions: boolean
}

const LoginScreen = ({ hasPermissions }: Props) => {
  const loginQuery = gql`
    mutation Login($email: String!, $password: String!) {
      login(email: $email, password: $password) {
        code
        user {
          id
          name
        }
        success
        exp
      }
    }
  `
  const verifyMfaMutation = gql`
    mutation ExchangeMFACode($mfaCode: String!, $rememberMe: Boolean) {
      exchangeMFACode(mfaCode: $mfaCode, rememberMe: $rememberMe) {
        success
        code
        user {
          id
          name
          email
          roles
          companiesId
          storesId
          __typename
        }
        iat
        exp
        __typename
      }
    }
  `
  const logoutMutation = gql`
    mutation Logout {
      logout
    }
  `

  const [, login] = useMutation(loginQuery)
  const [, verifyMfa] = useMutation(verifyMfaMutation)
  const [, logout] = useMutation(logoutMutation)
  const showDisplayLogo = useMediaQuery(theme.breakpoints.up('lg'))

  const [showLoginFailedMessage, setShowLoginFailedMessage] = useState(false)
  const [isMfaPending, setIsMfaPending] = useState(false)
  const [isMfaLoading, setIsMfaLoading] = useState(false)
  const [mfaError, setMfaError] = useState<string | null>(null)

  const attemptLogin = (email: string, password: string) => {
    setShowLoginFailedMessage(false)
    setMfaError(null)
    login(
      { email, password },
      {
        fetchOptions: {
          credentials: 'include',
          mode: 'cors'
        }
      }
    )
      .then((result) => {
        const code = result.data?.login.code
        if (code === AUTH_STATUS.MFA_STATUS_ENROLLED) {
          // Treat as intermediate valid state
          localStorage.clear()
          setIsMfaPending(true)
          setShowLoginFailedMessage(false)
        } else if (code !== AUTH_STATUS.LOGIN_SUCCESS) {
          setShowLoginFailedMessage(true)
        } else {
          // get cookie from response header

          const userId = result.data.login.user.id
          const userName = result.data.login.user.name

          localStorage.setItem('user-id', userId)
          localStorage.setItem('user-name', userName)
          window.location.reload()
        }
      })
      .catch((error) => {
        setShowLoginFailedMessage(true)
        console.error(error)
      })
  }

  const handleVerifyMfa = async ({
    mfaCode,
    rememberMe
  }: {
    mfaCode: string
    rememberMe: boolean
  }) => {
    setIsMfaLoading(true)
    setMfaError(null)
    try {
      const result = await verifyMfa(
        { mfaCode, rememberMe },
        {
          fetchOptions: {
            credentials: 'include',
            mode: 'cors'
          }
        }
      )
      const response = result.data?.exchangeMFACode
      if (response?.code === AUTH_STATUS.LOGIN_SUCCESS) {
        const userId = response.user.id
        const userName = response.user.name
        localStorage.setItem('user-id', userId)
        localStorage.setItem('user-name', userName)
        window.location.reload()
      } else {
        setMfaError('Invalid verification code. Please try again.')
      }
    } catch (_e) {
      setMfaError('Unable to verify code. Please try again.')
    } finally {
      setIsMfaLoading(false)
    }
  }

  const handleBackToLogin = async () => {
    setIsMfaPending(false)
    setMfaError(null)
    try {
      await logout()
    } catch (_e) {
      // ignore
    }
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh'
      }}
      display="flex"
      flexDirection="row"
      justifyContent="center"
    >
      <Box
        sx={{
          maxWidth: '960px',
          width: {
            xs: '100%',
            sm: '100%',
            md: '100%',
            lg: '45%',
            xl: '45%'
          }
        }}
        display="flex"
        flexDirection="column"
      >
        <img
          src="/images/grocery-tv-banner-logo.svg"
          alt="Grocery TV"
          height="17px"
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px'
          }}
        />
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          flexGrow={1}
        >
          <Box
            sx={{
              width: {
                xs: 'auto',
                sm: '480px',
              },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            {!isMfaPending ? (
              <LoginForm
                onSubmit={(data) => {
                  attemptLogin(data.email, data.password)
                }}
                onFormChange={() => {
                  if (showLoginFailedMessage) {
                    setShowLoginFailedMessage(false)
                  }
                }}
                showLoginFailedMessage={showLoginFailedMessage}
                hasPermissions={hasPermissions}
              />
            ) : (
              <MfaVerification
                onSubmit={handleVerifyMfa}
                onBackToLogin={handleBackToLogin}
                isLoading={isMfaLoading}
                error={mfaError}
              />
            )}
          </Box>
        </Box>
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="row"
          marginBottom="24px"
        >
          <Copyright />
        </Box>
      </Box>
      {showDisplayLogo && (
        <Box display="flex" flexGrow={1}>
          <LogoDisplay />
        </Box>
      )}
    </Box>
  )
}

export default LoginScreen
